import {
  MOBILE_SCROLL_POSITION_DELAY_VALUE,
  KEY_DENY_COUNT, INT_RADIX,
  OPT_INS,
  MOBILE_DELTA_VALUE,
  MOBILE_EXIT_INTENT_LOCATION_HASH,
  BANNER_BEHAVIOUR,
  WEB_BANNER
} from '../const/common';
import {
  device
} from './env';
import { matchElementPathAndDomain } from './matchElementPathAndDomain';
import { log } from './debug';
import Event from '../const/event';

let lastDropdownOpenAt = 0;
const DROPDOWN_IGNORE_DURATION = 30000;

export function initDropdownDetection() {
  const selectElements = document.querySelectorAll('select');

  const addDropdownListeners = (element) => {
    const events = ['click', 'touchstart', 'focus', 'mousedown', 'keydown'];

    events.forEach((eventType) => {
      element.addEventListener(eventType, () => {
        lastDropdownOpenAt = Date.now();
      });
    });
  };

  selectElements.forEach((select) => {
    addDropdownListeners(select);
  });

  document.addEventListener('click', (e) => {
    const target = e.target;

    const isDropdownLike = target.matches('select') || target.closest('select');

    if (isDropdownLike) {
      lastDropdownOpenAt = Date.now();
    }
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const newSelects = node.querySelectorAll('select');
            if (newSelects.length > 0) {
              newSelects.forEach((select) => {
                addDropdownListeners(select);
              });
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

export function markElementAsShown(banner, storageKey) {
  if (!banner) {
    return;
  }
  // Note: EVERY_X_DAYS frequency is handled via timestamp tracking in bannerShownXTimeUpdate (initWebBanners.js)
  if (banner?.frequency === BANNER_BEHAVIOUR.ONCE) {
    const storedBanners = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
    if (!storedBanners.includes(banner._id)) {
      const newShownElements = [...(JSON.parse(localStorage.getItem(storageKey) ?? '[]')), banner._id];
      localStorage.setItem(storageKey, JSON.stringify(newShownElements));
    }
  } else if (banner?.frequency === BANNER_BEHAVIOUR.ONCE_PER_SESSION || banner?.frequency === BANNER_BEHAVIOUR.EVERY_X_SESSIONS) {
    const storedBanners = JSON.parse(sessionStorage.getItem(storageKey) ?? '[]');
    if (!storedBanners.includes(banner._id)) {
      const newShownElements = [...(JSON.parse(sessionStorage.getItem(storageKey) ?? '[]')), banner._id];
      sessionStorage.setItem(storageKey, JSON.stringify(newShownElements));
    }
    if (banner?.frequency !== BANNER_BEHAVIOUR.EVERY_X_SESSIONS) {
      return;
    }
    const sessionKey = `${storageKey}-sessions-${banner._id}`;
    localStorage.setItem(sessionKey, 1);
    sessionStorage.setItem(sessionKey, 1);
  }
}

let lastPosition = window.scrollY;
export function checkMobileScrollingPosition() {
  let timer;
  let delta;

  const delay = MOBILE_SCROLL_POSITION_DELAY_VALUE;

  function clearMobileScrollPosition() {
    lastPosition = window.scrollY;
    delta = 0;
  }
  return () => {
    const newPosition = window.scrollY;
    if (lastPosition != null) {
      delta = newPosition - lastPosition;
    }

    lastPosition = newPosition;
    clearTimeout(timer);
    timer = setTimeout(clearMobileScrollPosition, delay);

    return delta;
  };
}

export async function applyConfirmAlertFilter(filter, configParam, context) {
  if (typeof filter.autoRegister !== 'undefined') {
    context.config.autoRegister = filter.autoRegister;
  }
  if (typeof filter.minimumVisits !== 'undefined') {
    context.config.alertMinimumVisits = filter.minimumVisits;
  }
  if (typeof filter.scrollPercentage !== 'undefined') {
    context.config.alertScrollPercentage = filter.scrollPercentage;
  }
  if (typeof filter.timeout !== 'undefined') {
    context.config.alertTimeout = filter.timeout * 1000;
  }
  if (typeof filter.localizationTitle !== 'undefined') {
    context.config.alertLocalization = { ...context.config.alertLocalization, title: filter.localizationTitle };
  }
  if (typeof filter.localizationInfo !== 'undefined') {
    context.config.alertLocalization = { ...context.config.alertLocalization, info: filter.localizationInfo };
  }
  if (typeof filter.radioButtonTopics !== 'undefined') {
    context.config.confirmAlertRadioButtonTopics = filter.radioButtonTopics;
  }
  if (typeof filter.requireTopics !== 'undefined') {
    context.config.confirmAlertRequireChannelTopics = filter.requireTopics;
  }
  if (typeof filter.nativeTheme !== 'undefined') {
    context.config.confirmAlertNativeTheme = filter.nativeTheme;
  }

  if (typeof filter.id !== 'undefined') {
    context.config.filterId = filter.id;
  }

  if (typeof filter.customCss !== 'undefined') {
    if (!context.config.customCss) {
      context.config.customCss = '';
    }
    context.config.customCss += filter.customCss;
  }
  if (typeof filter.config === 'object') {
    Object.keys(filter.config || {}).forEach((key) => {
      if (key === 'alertLocalization') {
        context.config[key] = { ...context.config[key], ...filter.config[key] };
      } else if (key.indexOf('alertLocalization') === 0) {
        if (!context.config.alertLocalization) {
          context.config.alertLocalization = {};
        }
        const keySub = key.substr('alertLocalization'.length);
        context.config.alertLocalization[keySub.substr(0, 1).toLowerCase() + keySub.substr(1)] = filter.config[key];
      } else if (key === 'serviceWorkerPath') {
        context.config.serviceWorkerFile = filter.config[key];
      } else if (key === 'customCss') {
        if (!context.config.customCss) {
          context.config.customCss = '';
        }
        context.config.customCss += filter.config.customCss;
      } else {
        context.config[key] = filter.config[key];
      }
    });
    if (filter.config.alertTimeout && filter.config.alertTimeout < 1000) {
      context.config.alertTimeout = filter.config.alertTimeout * 1000;
    }

    if (filter.config && filter.config.facebookOptIn && context.browserType !== 'facebook') {
      context.browserType = 'facebook';
      import('./setFacebookChannelConfig').then(({ setFacebookChannelConfig }) => {
        setFacebookChannelConfig(configParam, context.config, context.api);
      });
      context.importSubscriptionManager('facebookManager', filter.type);
    }
  }
}

/**
 * Determines whether a given filter object meets certain conditions.
 * @param {Object} filter
 * @param {Object} configParams
 * @returns {Boolean}
 *
 */

export function checkFilterConditions(filter, configParams = {}, context, isForTest = false) {
  switch (filter.type) {
    case 'device': {
      const isDesktop = filter.value === 'desktop';
      const isMobile = filter.value === 'mobile';
      const isBot = filter.value === 'bot';
      const isNotMobile = device.type !== 'mobile';
      const userAgent = navigator.userAgent;
      return (isDesktop && isNotMobile) || (isMobile && !isNotMobile) || (isBot && /bot|google|baidu|bing|msn|duckduckbot|teoma|slurp|yandex/i.test(userAgent));
    }
    case 'referrer': {
      const referrer = context.referrer || document.referrer;
      return referrer && new RegExp(filter.value).test(referrer);
    }
    case 'path': {
      const { pathname } = location;
      return pathname && new RegExp(filter.value).test(pathname);
    }
    case 'function': {
      try {
        const matches = !!eval(filter.value);
        return matches;
      } catch (err) {
        log.debug(err);
        return false;
      }
    }
    case 'userAgent': {
      const userAgent = navigator.userAgent;
      return userAgent && new RegExp(filter.value).test(userAgent);
    }
    case 'host': {
      const { hostname } = location;
      return hostname && new RegExp(filter.value).test(hostname);
    }
    case 'utmSource':
    case 'utmMedium':
    case 'utmCampaign': {
      const { search } = window.location;
      const utmParam = `utm_${filter.type.replace('utm', '').toLowerCase()}`;
      return search && search.indexOf(`${utmParam}=${filter.value}`) > -1;
    }
    case 'facebook':
    case 'wallet': {
      return context.browserType === filter.type;
    }
    case 'denyCountGreaterThan': {
      const { value } = filter;
      const denyCount = parseInt(localStorage.getItem(KEY_DENY_COUNT), INT_RADIX) || 0;
      return denyCount >= Number(value);
    }

    case 'filterGroup': {
      const { children } = filter;
      if (children && children.length === 0) {
        return false;
      }

      const matchedFilters = (children || []).filter((child) => checkFilterConditions(child, configParams, context));

      if (isForTest) {
        return matchedFilters.length === children.length;
      }

      if (matchedFilters.length === children.length) {
        applyConfirmAlertFilter(filter, configParams, context);
      }
    }
    /* falls through */
    default: {
      log.debug(`Invalid filter type: ${filter.type}`);
      return false;
    }
  }
}

export function initMobileExitIntentListeners(elementType, shownElement, element, elementHTML, shownElements, storageKey, context) {
  const backButtonExitIntentAllowed = context.isMobile && element.exitIntentForMobile && element.backButtonForMobileExitIntent;
  const scrollingExitIntentAllowed = context.isMobile && element.exitIntentForMobile && element.scrollingForMobileExitIntent;

  let backButtonPressed = false;
  let exitIntentTriggered = false;

  initDropdownDetection();

  const tryToShowBanner = async () => {
    if (exitIntentTriggered) {
      return;
    }
    exitIntentTriggered = true;

    let show = !shownElement;

    if (elementType !== OPT_INS) {
      show = await matchElementPathAndDomain(show, element);
    }

    const displayDocument = {
      webBanner: Event.BANNER_SHOWN,
      widget: Event.WIDGET_SHOWN,
      optIns: Event.OPTIN_SHOWN
    }[elementType];

    if (!displayDocument) {
      return;
    }

    if (elementType === OPT_INS) {
      context.triggerOptIn(true);
    }

    if (show) {
      if (elementType === WEB_BANNER) {
        context.hasExitIntentForWebBanner = true;
      }
      context.displayElement(elementHTML, shownElements, element, storageKey, displayDocument);
    }
  };

  const checkScrollSpeed = () => {
    const currentTime = Date.now();

    if (lastDropdownOpenAt > 0 && currentTime - lastDropdownOpenAt < DROPDOWN_IGNORE_DURATION) {
      return;
    }

    const getScrollDelta = checkMobileScrollingPosition();
    const delta = getScrollDelta();
    if (backButtonPressed || delta < MOBILE_DELTA_VALUE) {
      tryToShowBanner();
    }
  };

  const onBackButton = () => {
    backButtonPressed = true;

    if (location.hash !== MOBILE_EXIT_INTENT_LOCATION_HASH) {
      return;
    }

    history.replaceState(null, document.title, location.pathname);

    tryToShowBanner();
  };

  history.replaceState(null, document.title, `${location.pathname}${MOBILE_EXIT_INTENT_LOCATION_HASH}`);
  history.pushState(null, document.title, location.pathname);
  if (scrollingExitIntentAllowed) {
    window.addEventListener('scroll', checkScrollSpeed);
  }

  if (backButtonExitIntentAllowed) {
    window.addEventListener('popstate', onBackButton);
  }

  return backButtonPressed;
}
